import React, { useState, useEffect } from "react";
import { Check, Star, ArrowRight, Package as PackageIcon } from "lucide-react";
import packagesService from "./services/packagesService";

export default function PackagesSection() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPackages() {
      try {
        setLoading(true);
        const data = await packagesService.getActive();
        setPackages(data || []);
      } catch (err) {
        console.error("Error loading public packages:", err);
      } finally {
        setLoading(false);
      }
    }
    loadPackages();
  }, []);

  const formatPrice = (price, currency = "INR") => {
    if (!price) return "Custom Quote";
    const num = Number(price);
    const symbol = currency === "INR" ? "₹" : "$";
    return `${symbol} ${num.toLocaleString("en-IN")}`;
  };

  return (
    <section
      id="packages"
      className="relative py-24 bg-gradient-to-b from-slate-50 to-white overflow-hidden"
    >
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#9BB7A3]/5 rounded-full blur-[140px]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#9BB7A3] font-bold tracking-[4px] uppercase text-xs sm:text-sm block">
            Tailored Packages
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-800 mt-3">
            Pricing & Service Packages
          </h2>
          <div className="w-20 h-1 bg-[#9BB7A3] mx-auto mt-4 rounded-full"></div>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">
            Transparent pricing suites for residential homes, corporate offices, and customized turnkey renovations.
          </p>
        </div>

        {/* Packages Cards Grid */}
        {loading ? (
          <div className="py-20 flex justify-center items-center">
            <div className="w-10 h-10 border-3 border-[#9BB7A3]/30 border-t-[#9BB7A3] rounded-full animate-spin"></div>
          </div>
        ) : packages.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 p-8 max-w-md mx-auto shadow-sm">
            <div className="w-16 h-16 rounded-2xl bg-[#9BB7A3]/10 text-[#9BB7A3] flex items-center justify-center mx-auto mb-4">
              <PackageIcon size={32} />
            </div>
            <h3 className="text-lg font-bold text-slate-800">Packages Coming Soon</h3>
            <p className="text-xs text-slate-500 mt-1">
              Contact us directly to receive a customized quote for your space.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`
                  relative
                  rounded-[32px]
                  p-8
                  flex flex-col justify-between
                  transition-all duration-500
                  ${
                    pkg.popular
                      ? "bg-slate-900 text-white shadow-2xl scale-105 border-2 border-[#9BB7A3] z-20"
                      : "bg-white text-slate-800 shadow-lg border border-slate-200/80 hover:shadow-xl hover:-translate-y-2"
                  }
                `}
              >
                {/* Popular Pill Badge */}
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#9BB7A3] text-slate-950 text-xs font-black uppercase tracking-wider py-1.5 px-4 rounded-full shadow-md flex items-center gap-1">
                    <Star size={12} fill="currentColor" /> Most Popular Choice
                  </div>
                )}

                <div>
                  <h3
                    className={`text-2xl font-black ${
                      pkg.popular ? "text-white" : "text-slate-800"
                    }`}
                  >
                    {pkg.name}
                  </h3>
                  <p
                    className={`text-xs sm:text-sm mt-2 line-clamp-2 ${
                      pkg.popular ? "text-slate-300" : "text-slate-500"
                    }`}
                  >
                    {pkg.description || "Comprehensive bespoke interior & architectural package."}
                  </p>

                  {/* Price Box */}
                  <div className="my-6 pb-6 border-b border-slate-200/20">
                    <div className="flex items-baseline gap-1">
                      <span
                        className={`text-3xl sm:text-4xl font-black ${
                          pkg.popular ? "text-[#9BB7A3]" : "text-slate-900"
                        }`}
                      >
                        {formatPrice(pkg.price, pkg.currency)}
                      </span>
                    </div>
                    <span
                      className={`text-[11px] font-bold uppercase tracking-wider ${
                        pkg.popular ? "text-slate-400" : "text-slate-400"
                      }`}
                    >
                      Turnkey Estimate
                    </span>
                  </div>

                  {/* Features List */}
                  <div className="space-y-3 mb-8">
                    <span
                      className={`text-xs font-extrabold uppercase tracking-wider block mb-4 ${
                        pkg.popular ? "text-slate-400" : "text-slate-400"
                      }`}
                    >
                      Package Inclusions:
                    </span>
                    {Array.isArray(pkg.features) && pkg.features.length > 0 ? (
                      pkg.features.map((feat, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div
                            className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                              pkg.popular
                                ? "bg-[#9BB7A3] text-slate-950"
                                : "bg-[#9BB7A3]/20 text-[#9BB7A3]"
                            }`}
                          >
                            <Check size={12} strokeWidth={3} />
                          </div>
                          <span
                            className={`text-xs sm:text-sm font-medium leading-relaxed ${
                              pkg.popular ? "text-slate-200" : "text-slate-700"
                            }`}
                          >
                            {feat}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-400">Complete interior styling inclusions.</p>
                    )}
                  </div>
                </div>

                {/* Package CTA */}
                <a
                  href={`#contact`}
                  className={`
                    w-full py-4 rounded-2xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all duration-300 shadow-md no-underline
                    ${
                      pkg.popular
                        ? "bg-[#9BB7A3] text-slate-950 hover:bg-[#86a38e] hover:scale-[1.02]"
                        : "bg-slate-900 text-white hover:bg-[#9BB7A3] hover:text-white"
                    }
                  `}
                >
                  <span>Book This Package</span>
                  <ArrowRight size={15} />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
